import { CameraOff, Video } from 'lucide-react';
import { CameraFeed } from './camera-feed';

interface MinimalCameraViewerProps {
  cameras: Array<{ id: string; name: string }>;
  selectedCameraId: string | null;
  active: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onSelectCamera: (id: string) => void;
  /** 'hover' (default) hides controls until hover; 'always' keeps them visible */
  showControls?: 'hover' | 'always';
  className?: string;
  token?: string | null;
}

export function MinimalCameraViewer({
  cameras,
  selectedCameraId,
  active,
  onActivate,
  onDeactivate,
  onSelectCamera,
  showControls = 'hover',
  className,
  token,
}: MinimalCameraViewerProps) {
  const activeCameraId = selectedCameraId ?? cameras[0]?.id ?? null;
  const base = 'group relative h-full w-full overflow-hidden rounded-xl border border-border bg-surface-muted';

  return (
    <div className={className ? `${base} ${className}` : base}>
      {!active ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted">
          <CameraOff className="h-8 w-8" />
          <button
            onClick={onActivate}
            className="px-4 py-1.5 rounded-lg bg-accent text-white text-xs font-semibold hover:opacity-90 transition-opacity"
          >
            Connect
          </button>
        </div>
      ) : (
        <>
          {activeCameraId != null ? (
            <CameraFeed
              cameraId={activeCameraId}
              token={token}
              alt="Camera feed"
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-muted">
              <Video className="h-8 w-8" />
            </div>
          )}
          <div className={`absolute inset-x-0 top-0 flex items-center justify-between gap-2 bg-black/60 px-3 py-1.5 transition-opacity ${showControls === 'always' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <select
              value={activeCameraId ?? ''}
              onChange={(e) => onSelectCamera(e.target.value)}
              className="flex-1 bg-transparent text-xs text-white outline-none"
            >
              {cameras.map((c) => (
                <option key={c.id} value={c.id} className="bg-gray-900 text-white">
                  {c.name}
                </option>
              ))}
            </select>
            <button
              className="shrink-0 text-xs text-white/70 hover:text-white"
              onClick={onDeactivate}
            >
              Disconnect
            </button>
          </div>
        </>
      )}
    </div>
  );
}
